import React, { useEffect, useState } from 'react';
import ProductsTable from '../../components/inventory/ProductsTable';
import { arrayHasItems, scrollToTop } from '../../helpers/common';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import FilterDropdown from '../../components/FilterDropdown';
import { BOOLEAN_FILTERS, enumCommonBooleanStatuses, NO, YES } from '../../consts/common';
import { ACTIVE, enumInventoryPhasedStatus, IN_ACTIVE, PHASED_STATUSES, SUNSET, PLANNED } from '../../consts/inventoryStatus';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Pagination } from '../../components/Pagination';
import { useDebounce } from '../../helpers/useDebounce';
import { usePageReset } from '../../helpers/usePageReset';
import { ROUTES } from '../../consts/general';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { getGroupsData, ProductGroupLoadingKeys } from '../../slices/productGroupSlice';
import { CategoriesLoadingKeys, cleanUpCategoriesData, getCategoryIdsRequest } from '../../slices/categoriesSlice';

const Products = ({
  testId,
  access
}) => {

  const {
    groups,
    loading,
    pagination,
    isFirstLoadComplete,
    loadingKeys
  } = useSelector((state) => state.productGroup);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState(pageParams?.get('variantStatuses')?.split(',') || [ACTIVE, IN_ACTIVE, SUNSET, PLANNED]);
  const [selectedCategories, setSelectedCategories] = useState(
    arrayHasItems(pageParams?.get('categories')?.split(',')) ? 
    pageParams?.get('categories')?.split(',').map((category) => Number(category)) : 
    []
  );
  const [openedCreationVariant, setOpenedCreationVariant] = useState("");
  const [selectedAmazonStatuses, setSelectedAmazonStatuses] = useState(pageParams?.get('amazon')?.split(',') || [YES, NO]);
  const [selectedKitStatuses, setSelectedKitStatuses] = useState(pageParams?.get('kit')?.split(',') || [YES, NO]);
  const [searchTerm, setSearchTerm] = useState(pageParams?.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(pageParams?.get('page') || 1));
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get('itemsPerPage') || 25));
  const currentSearchValue = useDebounce(searchTerm, 300);
  
  // Auto-reset page to 1 when non-page params change
  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/group-data');

  const { 
    categoryOptions,
    loadingKeys: categoriesLoadingKeys,
    firstLoadComplete: categoriesFirstLoadComplete
  } = useSelector((state) => state.categories);

  const categoriesIsLoading = categoriesLoadingKeys.includes(CategoriesLoadingKeys.getCategoryIds)

  useEffect(() => {
    dispatch(getCategoryIdsRequest());
    return () => {
      dispatch(cleanUpCategoriesData({}));
    }
  }, []);

  const getSelectedCategories = () => {
    return selectedCategories.map(category => categoryOptions.find(opt => opt.id === category)?.id).filter(Boolean);
  }

  const onCloseProductCreation = () => {
    setOpenedCreationVariant("")
  }

  const openVariantSidebarByClickOnGroup = () => {
    setOpenedCreationVariant('variant');
  }

  const getGroups = async () => {
    const selectedCategoryIds = getSelectedCategories();
    dispatch(getGroupsData({
      search: currentSearchValue,
      page: currentPage, 
      itemsPerPage: currentItemsPerPage,
      categories: selectedCategoryIds,
      variantStatuses: selectedPhasedStatuses,
      amazon: selectedAmazonStatuses,
    }));
  }

  useEffect(() => {
    if (!categoriesIsLoading && categoriesFirstLoadComplete) {
      const selectedCategoryIds = getSelectedCategories();
      
      if (currentPage === 1 && shouldSkipRequest()) {
        const params = new URLSearchParams({
          search: currentSearchValue,
          page: currentPage, 
          itemsPerPage: currentItemsPerPage,
          categories: selectedCategoryIds,
          variantStatuses: selectedPhasedStatuses,
          amazon: selectedAmazonStatuses,
        });
        navigate(`${ROUTES.PRODUCTS}?${params}`, { replace: true });
        return;
      }
      
      getGroups();
      const params = new URLSearchParams({
        search: currentSearchValue,
        page: currentPage, 
        itemsPerPage: currentItemsPerPage,
        categories: selectedCategoryIds,
        variantStatuses: selectedPhasedStatuses,
        amazon: selectedAmazonStatuses,
      });
      navigate(`${ROUTES.PRODUCTS}?${params}`, { replace: true })
    }
  }, [
    currentSearchValue,
    currentPage,
    currentItemsPerPage,
    selectedCategories,
    selectedPhasedStatuses,
    selectedAmazonStatuses,
    categoriesFirstLoadComplete,
    categoriesIsLoading
  ]);

  useEffect(() => {
    scrollToTop();
  }, [currentPage, currentItemsPerPage]);

  const currentIsFirstLoadComplete = isFirstLoadComplete;
  const productsIsLoading = 
    loading && loadingKeys.includes(ProductGroupLoadingKeys.getGroupData) ||
    loading && loadingKeys.includes(ProductGroupLoadingKeys.createGroup) || 
    loading && loadingKeys.includes(ProductGroupLoadingKeys.createVariant) || 
    loading && loadingKeys.includes(ProductGroupLoadingKeys.addVariantToGroup);

  return (
    <div className="p-8"> {/* Add page padding here */}
      <h1 className="text-2xl font-semibold mb-6">Products</h1>
      <div className="mb-6 flex justify-between gap-2 lg:gap-6 flex-wrap items-center">
        <div className="flex flex-wrap gap-2 mb-2">
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <div className="relative">
            <FilterDropdown
              options={(categoryOptions || []).map(opt => opt.id)}
              selectedOptions={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Categories"
              renderOption={(categoryId) => {
                const categoryOption = categoryOptions.find(opt => opt.id === categoryId);
                return categoryOption ? (categoryOption.label ?? categoryOption.value ?? '') : '';
              }}
              testId={testId ? `${testId}_category_filter` : undefined}
            />
          </div>
          <div className="relative">
            <FilterDropdown
              options={PHASED_STATUSES}
              selectedOptions={selectedPhasedStatuses}
              onChange={setSelectedPhasedStatuses}
              placeholder="Variant Status"
                renderOption={(phase) => (
                <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
              )}
              testId={testId ? `${testId}_phased_filter` : undefined}
            />
          </div>
          <div className="relative">
            <FilterDropdown
              options={BOOLEAN_FILTERS}
              selectedOptions={selectedAmazonStatuses}
              onChange={setSelectedAmazonStatuses}
              placeholder="Is Amazon"
              renderOption={(status) => (
                <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
              )}
              testId={testId ? `${testId}_amazon_filter` : undefined}
            />
          </div>
        </div>
        {access === "edit" && (
          <div className="flex flex-wrap gap-2 mb-2">
              <button 
                className="inline-flex items-center h-fit px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-gray-800"
                onClick={() => setOpenedCreationVariant('group')}
              >
                Create Product
              </button>
          </div>
        )}
      </div>
      <div className="overflow-hidden relative">
        {currentIsFirstLoadComplete && productsIsLoading && <OverlayGrayBackground />}
        {!currentIsFirstLoadComplete && (
          <div className="z-10 h-60">
            <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
          </div>
        )}
        {currentIsFirstLoadComplete && (
          <ProductsTable 
            products={groups}
            access={access}
            openCreationVariant={openedCreationVariant}
            openVariantSidebarCallback={openVariantSidebarByClickOnGroup}
            onSaveCreation={() => {
              if (openedCreationVariant === "group") {
                getGroups();
                onCloseProductCreation();
              }
              if (openedCreationVariant === "variant") {
                getGroups();
                onCloseProductCreation();
              }
            }}
            onCloseProductCreation={() => onCloseProductCreation()}
          />
        )}
      </div>
      {currentIsFirstLoadComplete && (
        <div className="mt-6 mb-4">
          <Pagination
            currentItemsPerPage={currentItemsPerPage}
            rangeRows={pagination.rangeRows}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isTheLastPage={pagination.isTheLastPage}
            totalPages={pagination.totalPages}
            setCurrentItemsPerPage={setCurrentItemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default Products; 